// oxlint-disable no-console
'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { memo, useEffect, useRef } from 'react';

import { PopupId, PopupPage } from '@flipster/apis/v1/popup/types';
import { OverlayProps, useOverlay } from '@flipster/overlay';

import { PopupContext, entryPopupBunja } from './bunja';
import { AffiliateDashboardRenewalModal } from './modal/affiliate-dashboard-renewal-modal';
import { FeatureHighlightPopup } from './modal/feature-highlight';
import { FollowOnboardingModal } from './modal/follow';
import KycPopup from './modal/kyc';
import { LeagueLeaderboardOnboardingModal } from './modal/league-leaderboard';
import { LeagueMainOnboardingModal } from './modal/league-main';
import { LeverageLimitRestrictPopup } from './modal/leverage-limit-restrict';
import MarginBonusExpirationDialog from './modal/margin-bonus-expiration';

const nodes: Record<PopupId, (props: OverlayProps) => React.ReactNode> = {
  WELCOME_TO_TIERED_LEAGUE: ({ isOpen, close, unmount }) => (
    <LeagueMainOnboardingModal isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
  TIERED_LEAGUE_DESCRIPTION: ({ isOpen, close, unmount }) => (
    <LeagueLeaderboardOnboardingModal isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
  ADHOC_FOLLOW_ONBOARDING: ({ isOpen, close, unmount }) => (
    <FollowOnboardingModal isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
  NEW_AFFILIATE_DASHBOARD: ({ isOpen, close, unmount }) => (
    <AffiliateDashboardRenewalModal isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
  ADHOC_KYC_REMINDER: ({ isOpen, close, unmount }) => <KycPopup isOpen={isOpen} onClose={close} onExit={unmount} />,
  ADHOC_FEATURE_HIGHLIGHT: ({ isOpen, close, unmount }) => (
    <FeatureHighlightPopup isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
  ADHOC_MARGIN_BONUS_EXPIRATION: ({ isOpen, close, unmount }) => (
    <MarginBonusExpirationDialog isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
  ADHOC_LEVERAGE_LIMIT_RESTRICT: ({ isOpen, close, unmount }) => (
    <LeverageLimitRestrictPopup isOpen={isOpen} onClose={close} onExit={unmount} />
  ),
};

export const EntryPopupPageSetter = ({ page }: { page: PopupPage }) => {
  const { setEntryPage } = useBunja(entryPopupBunja);

  useEffect(() => {
    setEntryPage(page);
    return () => setEntryPage(undefined);
  }, [page, setEntryPage]);

  return null;
};

export const EntryPopup = memo(() => {
  const overlay = useOverlay();
  const { popupAtom } = useBunja(entryPopupBunja);
  const popup = useAtomValue(popupAtom);
  const prevPopupIdRef = useRef<PopupId | undefined>(undefined);
  useEffect(() => {
    if (popup?.id && popup.id !== prevPopupIdRef.current) {
      prevPopupIdRef.current = popup.id;
      overlay.open((props) => <PopupContext value={popup}>{nodes[popup.id](props)}</PopupContext>);
    } else if (!popup?.id && prevPopupIdRef.current) {
      // popup이 없어지면 ref 초기화
      prevPopupIdRef.current = undefined;
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [popup?.id, overlay]);
  return null;
});
